import { ThunkDispatch } from "core-integration/src/store";
import {
  getElementControls,
  getElementsFromControls,
} from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
} from "core-integration/src/store/elements/thunks";
import { IElement } from "core-integration/src/store/elements/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { ITeam } from "core-integration/src/store/teams/types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import BoldLink from "../../BoldLink";
import ElementInTable from "../../ElementInTable";
import ElementStatus from "../../ElementStatus";
import { ElementRow } from "../../ElementTable";
import { VisuallyHidden } from "../../Utils";
import StatusPanel from "../StatusPanel";
import {
  MoreLinkWrap,
  PlayerAvailabilityHeading,
  PlayerCell,
  PlayersTable,
  RightCell,
} from "../styles";
import type { PlayerAvailabilityTableProps } from "./types";

const PlayerAvailabilityTable = ({
  playerCount = 20,
}: PlayerAvailabilityTableProps) => {
  const controls = useSelector(getElementControls);
  const dispatch = useDispatch<ThunkDispatch>();
  const { t } = useTranslation();
  const teamsById = useSelector(getTeamsById);
  const elements = useSelector(getElementsFromControls);

  useEffect(() => {
    dispatch(
      updateElementControlsAndMaxCost({
        ...controls,
        filter: "all",
        sort: "news_added",
        search: "",
      }),
    );
  }, []);

  const showElementDialog = (element: IElement) =>
    dispatch(showElementSummary(element.id));

  const availabilityElements = elements.data.slice(0, playerCount);

  return (
    <StatusPanel>
      <StatusPanel.Header>
        <PlayerAvailabilityHeading>
          {t("playerAvailability.heading", "Player Availability")}
        </PlayerAvailabilityHeading>
      </StatusPanel.Header>
      <StatusPanel.Body>
        <PlayersTable>
          <tbody>
            {availabilityElements.map((element) => {
              const team = teamsById[element.team] as ITeam;

              return (
                <ElementRow key={element.id}>
                  <PlayerCell>
                    <ElementInTable
                      element={element}
                      renderElementMenu={() => showElementDialog(element)}
                      team={team}
                    />
                  </PlayerCell>
                  <RightCell>
                    <span aria-hidden="true">
                      <ElementStatus $variant="list" element={element} />
                    </span>
                    <VisuallyHidden>
                      {element.news
                        ? element.news
                        : t("playerAvailability.available", "Available")}
                    </VisuallyHidden>
                  </RightCell>
                </ElementRow>
              );
            })}
          </tbody>
        </PlayersTable>
        <MoreLinkWrap>
          <BoldLink to="/the-scout/player-news">
            {t("status.viewMore", "View more")}
          </BoldLink>
        </MoreLinkWrap>
      </StatusPanel.Body>
    </StatusPanel>
  );
};

export default PlayerAvailabilityTable;
